
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/context/auth-context";
import { Badge } from "@/components/ui/badge";
import { UserCheck, Users, AlertTriangle, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";

type CourseRegistration = {
  id: string;
  status: string;
  attendance_date: string | null;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    photo_url: string | null;
  };
  courses: {
    title: string;
  };
};

const CheckIn = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const { 
    data: courses = [], 
    isLoading: isLoadingCourses,
    error: coursesError,
    isError: isCoursesError 
  } = useQuery({
    queryKey: ['instructor-courses'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('courses')
          .select('id, title')
          .eq('event_type', 'live')
          .eq('status', 'published')
          .order('start_date', { ascending: true });

        if (error) throw error;
        return data;
      } catch (error: any) {
        console.error('Error loading courses:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load courses",
          variant: "destructive",
        });
        throw error;
      }
    },
  });

  const { 
    data: registrations = [], 
    isLoading: isLoadingRegistrations,
    error: registrationsError,
    isError: isRegistrationsError,
    refetch: refetchRegistrations 
  } = useQuery({
    queryKey: ['course-registrations', selectedCourseId],
    enabled: !!selectedCourseId,
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('course_registrations')
          .select(`
            id,
            status,
            attendance_date,
            profiles!inner(first_name, last_name, photo_url),
            courses!inner(title)
          `)
          .eq('course_id', selectedCourseId)
          .eq('status', 'registered');

        if (error) throw error;
        return data as CourseRegistration[];
      } catch (error: any) {
        console.error('Error loading registrations:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load registrations",
          variant: "destructive",
        });
        throw error;
      }
    },
  });

  const handleVerifyAttendance = async (registrationId: string) => {
    setVerificationError(null);
    
    try {
      // Set loading state for this specific registration
      setLoadingStates(prev => ({ ...prev, [registrationId]: true }));
      
      const { error } = await supabase
        .from('course_registrations')
        .update({
          attendance_date: new Date().toISOString(),
          status: 'attended',
          verified_by: profile?.id,
          verified_at: new Date().toISOString()
        })
        .eq('id', registrationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Attendance verified successfully",
      });

      await refetchRegistrations();
    } catch (error: any) {
      console.error('Error verifying attendance:', error);
      setVerificationError(error.message || "Failed to verify attendance. Please try again.");
      toast({
        title: "Error",
        description: error.message || "Failed to verify attendance",
        variant: "destructive",
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [registrationId]: false }));
    }
  };

  // Updated to check for tech role instead of staff/admin
  if (!profile || !["tech", "superadmin"].includes(profile.role || "")) {
    return (
      <Card className="mx-auto max-w-md mt-8">
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
          <CardDescription>
            You don't have permission to access this page. This feature is only available to technical administrators.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="container pt-6 max-w-4xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Manual Attendance Verification</CardTitle>
              <CardDescription>
                Verify participant attendance using their photo ID
              </CardDescription>
            </div>
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          {isCoursesError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {coursesError instanceof Error 
                  ? coursesError.message 
                  : "Failed to load courses. Please refresh the page."}
              </AlertDescription>
            </Alert>
          )}

          {verificationError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{verificationError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            {isLoadingCourses ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Loading courses...</span>
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-4">
                <p>No live courses available for check-in.</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {courses.map((course) => (
                  <Button
                    key={course.id}
                    variant={selectedCourseId === course.id ? "default" : "outline"}
                    onClick={() => setSelectedCourseId(course.id)}
                  >
                    {course.title}
                  </Button>
                ))}
              </div>
            )}

            {selectedCourseId && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Registered Participants ({registrations.length})
                </h3>
                
                {isLoadingRegistrations ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading registrations...</span>
                  </div>
                ) : isRegistrationsError ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {registrationsError instanceof Error 
                        ? registrationsError.message 
                        : "Failed to load registrations. Please try again."}
                    </AlertDescription>
                  </Alert>
                ) : registrations.length === 0 ? (
                  <div className="text-center py-4 border rounded-md p-8">
                    <p>No participants registered for this course.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {registrations.map((registration) => (
                      <Card key={registration.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <Avatar className="h-16 w-16">
                                <AvatarImage 
                                  src={registration.profiles.photo_url || undefined} 
                                  alt={`${registration.profiles.first_name} ${registration.profiles.last_name}`}
                                />
                                <AvatarFallback className="text-lg">
                                  {registration.profiles.first_name?.[0]}
                                  {registration.profiles.last_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-lg">
                                  {registration.profiles.first_name} {registration.profiles.last_name}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {registration.courses.title}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {registration.attendance_date ? (
                                <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800">
                                  <UserCheck className="h-3 w-3" />
                                  Verified
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleVerifyAttendance(registration.id)}
                                  disabled={loadingStates[registration.id]}
                                >
                                  {loadingStates[registration.id] ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      Verifying...
                                    </>
                                  ) : (
                                    "Verify Attendance"
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckIn;
